// Honest, pure derivations for the Brain panels. Every value here is a REAL count
// or ratio computed from a real endpoint field. NO fabricated composites, NO deltas.

// PRUNE #1 (VP): Company Health is NOT a concept-art "82" composite with invented
// sub-scores. It is three honestly-labeled readings from real fields, presented
// side by side and explicitly NOT blended into one number.
//   - gap-severity distribution : GET /api/state/gaps  -> gaps[].gap.severity
//   - outcome win-rate          : GET /api/outcomes/summary -> by_result
//   - open campaign alerts      : GET /api/executive/queue -> summary.by_type.CAMPAIGN_ALERT
export function deriveCompanyHealth({ gaps, outcomesSummary, queueSummary } = {}) {
  const severity = { NONE: 0, MINOR: 0, MAJOR: 0, CRITICAL: 0 };
  let gapsAvailable = false;
  if (gaps && Array.isArray(gaps.gaps)) {
    gapsAvailable = true;
    for (const g of gaps.gaps) {
      const s = (g.gap && g.gap.severity) || "NONE";
      if (severity[s] == null) severity[s] = 0;
      severity[s] += 1;
    }
  }

  let winRate = null;
  let outcomesResolved = 0;
  if (outcomesSummary && outcomesSummary.by_result) {
    const r = outcomesSummary.by_result;
    const win = r.WIN || r.SUCCESS || 0;
    const loss = r.LOSS || r.FAILURE || 0;
    outcomesResolved = win + loss;
    winRate = outcomesResolved > 0 ? +(win / outcomesResolved).toFixed(4) : null;
  }

  let openAlerts = null;
  if (queueSummary && queueSummary.by_type) {
    openAlerts = queueSummary.by_type.CAMPAIGN_ALERT || 0;
  }

  return {
    // deliberately NO single score.
    gapSeverity: gapsAvailable ? severity : null,
    gapSeveritySource: "GET /api/state/gaps -> gaps[].gap.severity",
    winRate,
    outcomesResolved,
    winRateSource: "GET /api/outcomes/summary -> by_result",
    openAlerts,
    openAlertsSource: "GET /api/executive/queue -> summary.by_type.CAMPAIGN_ALERT",
  };
}

// PRUNE #2 (VP): current counts only. NO "+N this week" — no snapshot-diff source
// exists, so trend arrows are omitted, not invented.
export function deriveMomentum({ predictionsSummary, recommendationsSummary, observations, lessonsSummary, journalSummary, outcomesSummary } = {}) {
  const counters = [];
  const push = (label, value, source) => {
    if (value != null) counters.push({ label, value, source, trend: null });
  };
  push(
    "Active predictions",
    predictionsSummary ? predictionsSummary.active : null,
    "GET /api/predictions/summary -> active"
  );
  push(
    "Pending recommendations",
    recommendationsSummary ? recommendationsSummary.pending : null,
    "GET /api/recommendations/summary -> pending"
  );
  push(
    "Observations",
    Array.isArray(observations) ? observations.length : (observations && observations.total),
    "GET /api/intelligence/observations -> count"
  );
  push(
    "Lessons",
    lessonsSummary ? lessonsSummary.total : null,
    "GET /api/lessons/summary -> total"
  );
  push(
    "Journal entries",
    journalSummary ? journalSummary.total : null,
    "GET /api/journal/summary -> total"
  );
  if (outcomesSummary && outcomesSummary.by_result) {
    const r = outcomesSummary.by_result;
    const win = r.WIN || r.SUCCESS || 0;
    const loss = r.LOSS || r.FAILURE || 0;
    if (win + loss > 0) {
      counters.push({
        label: "Outcome win-rate",
        value: `${Math.round((win / (win + loss)) * 100)}%`,
        source: "GET /api/outcomes/summary -> by_result",
        trend: null,
      });
    }
  }
  return counters;
}

// Company Health V1 — a TRANSPARENT, declared composite (CTO-reinstated).
// It is the MEAN OF THE AVAILABLE real sub-scores. A sub-score whose capability is
// disabled/absent is EXCLUDED and shown "not counted" — NEVER imputed. Every input
// is inspectable; the number never appears without its inputs. Badge: V1 / evolving.
//
// Declared heuristic constant (transparent, tunable): each open campaign alert costs
// this many points off the delivery sub-score. Shown in the inputs, not hidden.
export const V1_ALERT_PENALTY = 10;

function clamp100(n) {
  return Math.max(0, Math.min(100, n));
}

export function deriveCompanyHealthV1({ buHealth, schedulers, gaps, queueSummary, outcomesSummary } = {}) {
  const sub = [];

  // 1. Infra health — % of BUs green (+ scheduler liveness when present).
  if (buHealth && Array.isArray(buHealth.business_units) && buHealth.business_units.length) {
    const bus = buHealth.business_units;
    const green = bus.filter((b) => b.status === "green").length;
    let value = (green / bus.length) * 100;
    let schedNote = "schedulers: n/a";
    if (schedulers && typeof schedulers === "object") {
      const ok = schedulers.status ? schedulers.status === "ok" || schedulers.status === "green" : undefined;
      if (ok === false) value = value * 0.75; // real degraded signal
      schedNote = `schedulers: ${schedulers.status != null ? schedulers.status : "present"}`;
    }
    sub.push({
      key: "infra",
      label: "Infrastructure",
      value: Math.round(clamp100(value)),
      available: true,
      inputs: { bus_green: green, bus_total: bus.length, note: schedNote },
      source: "GET /api/bu/health + /api/schedulers/health",
    });
  } else {
    sub.push({ key: "infra", label: "Infrastructure", value: null, available: false, source: "GET /api/bu/health", note: "not counted" });
  }

  // 2. Goal attainment — share of gaps at NONE/MINOR severity.
  if (gaps && Array.isArray(gaps.gaps)) {
    const rows = gaps.gaps;
    if (rows.length) {
      const good = rows.filter((g) => ["NONE", "MINOR"].includes((g.gap && g.gap.severity) || "NONE")).length;
      sub.push({
        key: "goals",
        label: "Goal attainment",
        value: Math.round((good / rows.length) * 100),
        available: true,
        inputs: { good, total: rows.length },
        source: "GET /api/state/gaps -> gaps[].gap.severity",
      });
    } else {
      sub.push({ key: "goals", label: "Goal attainment", value: null, available: false, note: "no active goals with targets", source: "GET /api/state/gaps" });
    }
  } else {
    sub.push({ key: "goals", label: "Goal attainment", value: null, available: false, note: "not counted", source: "GET /api/state/gaps" });
  }

  // 3. Delivery — inverse of open campaign alerts (declared penalty, transparent).
  if (queueSummary && queueSummary.by_type && queueSummary.by_type.CAMPAIGN_ALERT != null) {
    const alerts = queueSummary.by_type.CAMPAIGN_ALERT;
    sub.push({
      key: "delivery",
      label: "Delivery",
      value: Math.round(clamp100(100 - alerts * V1_ALERT_PENALTY)),
      available: true,
      inputs: { open_alerts: alerts, penalty_per_alert: V1_ALERT_PENALTY },
      source: "GET /api/executive/queue -> summary.by_type.CAMPAIGN_ALERT",
    });
  } else {
    sub.push({ key: "delivery", label: "Delivery", value: null, available: false, note: "not counted", source: "GET /api/executive/queue" });
  }

  // 4. Outcome quality — win-rate (only when resolved outcomes exist).
  if (outcomesSummary && outcomesSummary.by_result) {
    const r = outcomesSummary.by_result;
    const win = r.WIN || r.SUCCESS || 0;
    const loss = r.LOSS || r.FAILURE || 0;
    if (win + loss > 0) {
      sub.push({
        key: "outcomes",
        label: "Outcome quality",
        value: Math.round((win / (win + loss)) * 100),
        available: true,
        inputs: { win, loss },
        source: "GET /api/outcomes/summary -> by_result",
      });
    } else {
      sub.push({ key: "outcomes", label: "Outcome quality", value: null, available: false, note: "no resolved outcomes", source: "GET /api/outcomes/summary" });
    }
  } else {
    sub.push({ key: "outcomes", label: "Outcome quality", value: null, available: false, note: "not counted", source: "GET /api/outcomes/summary" });
  }

  const counted = sub.filter((s) => s.available && typeof s.value === "number");
  const score = counted.length ? Math.round(counted.reduce((a, s) => a + s.value, 0) / counted.length) : null;

  return {
    version: "V1",
    definition: "mean of available real sub-scores; disabled/absent sub-scores excluded (not imputed)",
    score, // null when NOTHING is available — never a fabricated fallback
    subScores: sub,
    countedKeys: counted.map((s) => s.key),
    notCounted: sub.filter((s) => !s.available).map((s) => s.key),
  };
}

// PRUNE #4 (VP): hero counter is a REAL sum of live summaries, or null (omit).
// Never the concept-art placeholder.
export function heroInsightsCount(parts = []) {
  const nums = parts.filter((n) => typeof n === "number" && !Number.isNaN(n));
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0);
}
